import { useState, useCallback, useEffect, useRef } from "react";
import { useFeaturedProducts, type Product } from "@/hooks/useProducts";
import ShopProductCard from "@/components/ShopProductCard";
import QuickViewModal from "@/components/QuickViewModal";
import { ChevronLeft, ChevronRight } from "lucide-react";

const FeaturedProducts = () => {
  const { data: products = [] } = useFeaturedProducts();
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<number | null>(null);

  const total = products.length;

  const next = useCallback(() => {
    if (total === 0) return;
    setCurrent((prev) => (prev + 1) % total);
  }, [total]);

  const prev = useCallback(() => {
    if (total === 0) return;
    setCurrent((prev) => (prev - 1 + total) % total);
  }, [total]);

  const goTo = useCallback(
    (index: number) => {
      if (total === 0) return;
      setCurrent(((index % total) + total) % total);
    },
    [total]
  );

  useEffect(() => {
    if (isPaused || total === 0) {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = window.setInterval(next, 3500);
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [isPaused, total, next]);

  // Position each card relative to the center
  const getOffset = (index: number) => {
    let diff = index - current;
    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;
    return diff;
  };

  const getCardStyle = (index: number): React.CSSProperties => {
    const offset = getOffset(index);
    const absOffset = Math.abs(offset);

    if (absOffset > 2) {
      return {
        position: "absolute",
        left: "50%",
        top: "50%",
        opacity: 0,
        pointerEvents: "none",
        transform: "translate(-50%, -50%) scale(0.5)",
        transition: "all 500ms cubic-bezier(0.22, 1, 0.36, 1)",
      };
    }

    const configs: Record<number, { scale: number; zIndex: number; opacity: number; grayscale: number; xPx: number }> = {
      0: { scale: 1, zIndex: 5, opacity: 1, grayscale: 0, xPx: 0 },
      1: { scale: 0.85, zIndex: 4, opacity: 0.7, grayscale: 1, xPx: 260 },
      2: { scale: 0.72, zIndex: 3, opacity: 0.35, grayscale: 1, xPx: 440 },
    };

    const config = configs[absOffset] ?? configs[2];
    const direction = offset < 0 ? -1 : 1;

    return {
      position: "absolute",
      left: "50%",
      top: "50%",
      transform: `translate(-50%, -50%) translateX(${direction * config.xPx}px) scale(${config.scale})`,
      zIndex: config.zIndex,
      opacity: config.opacity,
      filter: `grayscale(${config.grayscale})`,
      transition: "all 500ms cubic-bezier(0.22, 1, 0.36, 1)",
      pointerEvents: absOffset <= 1 ? "auto" : "none",
    };
  };

  if (total === 0) return null;

  return (
    <section className="py-20 bg-section-alt overflow-hidden">
      <div className="container">
        <div className="text-center mb-16">
          <span className="inline-block text-[10px] font-body font-semibold uppercase tracking-[0.25em] text-primary bg-primary/10 px-3 py-1 rounded-full mb-4">
            ★ Best Sellers
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">Featured Products</h2>
          <p className="text-muted-foreground font-body max-w-lg mx-auto">
            Our most loved styles — chosen by customers like you
          </p>
        </div>

        <div onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
          <div className="relative h-[520px] md:h-[600px]">
            {products.map((product, i) => {
              const offset = getOffset(i);
              const isCenter = offset === 0;

              return (
                <div
                  key={product.id}
                  style={getCardStyle(i)}
                  className={`w-[240px] md:w-[280px] cursor-pointer ${
                    isCenter ? "hover:-translate-y-1" : ""
                  }`}
                  onClick={() => {
                    if (isCenter) {
                      setQuickViewProduct(product);
                    } else {
                      goTo(i);
                    }
                  }}
                >
                  <div
                    className={`bg-card rounded-2xl overflow-hidden pb-3 transition-shadow duration-500 ${
                      isCenter
                        ? "shadow-[0_8px_40px_-8px_hsl(var(--primary)/0.35)] ring-1 ring-primary/20"
                        : "shadow-xl"
                    }`}
                  >
                    <ShopProductCard product={product} onQuickView={setQuickViewProduct} index={i} />
                  </div>
                </div>
              );
            })}

            <button
              onClick={prev}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background/80 backdrop-blur border border-border/50 flex items-center justify-center text-foreground/70 hover:text-foreground hover:bg-background transition-all shadow-lg"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <button
              onClick={next}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background/80 backdrop-blur border border-border/50 flex items-center justify-center text-foreground/70 hover:text-foreground hover:bg-background transition-all shadow-lg"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-4">
            {products.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === current ? "bg-primary w-6" : "bg-border hover:bg-muted-foreground"
                }`}
                aria-label={`Go to product ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      <QuickViewModal
        product={quickViewProduct}
        open={!!quickViewProduct}
        onOpenChange={(open) => !open && setQuickViewProduct(null)}
      />
    </section>
  );
};

export default FeaturedProducts;
